import { useEffect, useRef, useState } from "react";
import { initialPhoneInput, isBlankPhoneInput } from "../phone";
import { useTranslation } from "react-i18next";
import { Download, ExternalLink, KeyRound, MapPin, Phone, QrCode, Star } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, downloadFile, type FeedbackOverview } from "../api";
import { useBranch } from "../lib/use-branch";
import { locationPreviewUrl, mapCoordinates } from "../lib/map-location";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { PasswordInput } from "../components/password-input";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ServicesPage } from "./ServicesPage";
import { BranchesPage } from "./BranchesPage";
import { TariffPage } from "./TariffPage";
import { FinanceSettings } from "../components/finance-settings";
import { useFinanceText } from "../lib/finance-text";

export type SettingsSection =
  | "finance"
  | "umumiy"
  | "xizmatlar"
  | "filiallar"
  | "baholar"
  | "tarif";

/**
 * Sozlamalar — bitta nav item, ichida sub-sahifalar: Umumiy (QR/havola),
 * Xizmatlar (shablonlar), Filiallar (pro+) va Tarif. `initialSection` bilan
 * kerakli tabga ochiladi (masalan, upsell tugmasi → "tarif").
 */
export function SettingsPage({
  initialSection = "umumiy",
  onSectionChange,
}: {
  initialSection?: SettingsSection;
  onSectionChange?: (section: SettingsSection) => void;
}) {
  const [section, setSection] = useState<SettingsSection>(initialSection);
  const { t } = useTranslation("settings");
  const ft = useFinanceText();
  const selectSection = (next: SettingsSection) => {
    setSection(next);
    onSectionChange?.(next);
  };

  return (
    <Tabs
      value={section}
      onValueChange={(v) => {
        const next = v as SettingsSection;
        selectSection(next);
      }}
      className="settings-page space-y-4"
    >
      <TabsList>
        <TabsTrigger value="finance">{ft("options")}</TabsTrigger>
        <TabsTrigger value="umumiy">{t("tabs.general")}</TabsTrigger>
        <TabsTrigger value="xizmatlar">{t("tabs.services")}</TabsTrigger>
        <TabsTrigger value="filiallar">{t("tabs.branches")}</TabsTrigger>
        <TabsTrigger value="baholar">{t("tabs.feedback")}</TabsTrigger>
        <TabsTrigger value="tarif">{t("tabs.tariff")}</TabsTrigger>
      </TabsList>
      <TabsContent value="finance" className="mt-0"><FinanceSettings /></TabsContent>

      <TabsContent value="umumiy" className="mt-0">
        <GeneralSettings onOpenBranches={() => selectSection("filiallar")} />
      </TabsContent>
      <TabsContent value="xizmatlar" className="mt-0">
        <ServicesPage />
      </TabsContent>
      <TabsContent value="filiallar" className="mt-0">
        <BranchesPage onOpenTariff={() => selectSection("tarif")} />
      </TabsContent>
      <TabsContent value="baholar" className="mt-0">
        <div className="max-w-3xl">
          <FeedbacksCard />
        </div>
      </TabsContent>
      <TabsContent value="tarif" className="mt-0">
        <TariffPage />
      </TabsContent>
    </Tabs>
  );
}

export function GeneralSettings({ onOpenBranches }: { onOpenBranches: () => void }) {
  const { t } = useTranslation("settings");
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const qrWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { deep_link } = await api.workshopInviteLink();
        if (cancelled) return;
        setDeepLink(deep_link);
      } catch (e) {
        if (cancelled) return;
        setError(
          (e as { message?: string }).message || t("general.qr.genError"),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const downloadPng = async () => {
    if (!deepLink || !qrWrapRef.current) return;
    const svg = qrWrapRef.current.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const url = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const size = 1024;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "ustaxona-qr.png";
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(t("general.qr.pngDownloaded"));
      }, "image/png");
    };
    img.src = url;
  };

  const copyLink = () => {
    if (!deepLink) return;
    navigator.clipboard.writeText(deepLink).then(
      () => toast.success(t("general.qr.linkCopied")),
      () => toast.error(t("general.qr.copyError")),
    );
  };

  return (
    <div data-testid="general-settings-grid" className="settings-general-grid grid max-w-5xl gap-3 md:grid-cols-2">
      <WorkshopContactCard />
      <ChangePasswordCard />
      <div className="md:col-span-2"><WorkshopProfileCard onOpenBranches={onOpenBranches} /></div>
      <Card className="md:col-span-2" data-testid="qr-export-section">
        <CardHeader className="p-4 pb-3">
          <div className="flex items-center gap-2">
            <QrCode className="size-5 text-primary" />
            <CardTitle>{t("general.qr.title")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <p className="text-sm text-muted-foreground mb-3">
            {t("general.qr.desc")}
          </p>

          {loading && (
            <div className="py-3">
              <Skeleton className="size-36 rounded-md" />
            </div>
          )}

          {error && (
            <div className="text-sm text-destructive py-4">{error}</div>
          )}

          {deepLink && !loading && (
            <>
              <div>
                <div
                  ref={qrWrapRef}
                  className="p-2 bg-white rounded-md border inline-block"
                >
                  <QRCodeSVG value={deepLink} size={144} level="M" />
                </div>
              </div>

              <div className="mt-2 text-xs text-muted-foreground break-all bg-muted/60 rounded-md p-2 font-mono">
                {deepLink}
              </div>

              <div className="flex gap-2 mt-3 flex-wrap">
                <Button onClick={downloadPng} variant="default">
                  <Download />
                  {t("general.qr.downloadPng")}
                </Button>
                <Button onClick={copyLink} variant="outline">
                  {t("general.qr.copyLink")}
                </Button>
              </div>
            </>
          )}
          <DataExportCard />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * To'liq ma'lumot eksporti (data portability) — barcha tariflar, tarif gate
 * YO'Q (ExportButton/useFeature ishlatilmaydi — u Max'ga gate qiladi).
 */
function DataExportCard() {
  const { t } = useTranslation("settings");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      await downloadFile(
        "/owner/full-export.xlsx",
        `ustaxona-malumotlari-${date}.xlsx`,
      );
      toast.success(t("general.dataExport.done"));
    } catch (e) {
      toast.error(
        (e as { message?: string }).message || t("general.dataExport.error"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t pt-4">
      <div className="mb-2">
        <div className="flex items-center gap-2">
          <Download className="size-5 text-primary" />
          <h3 className="font-num text-[15px] font-bold">{t("general.dataExport.title")}</h3>
        </div>
      </div>
      <div>
        <p className="text-sm text-muted-foreground mb-4">
          {t("general.dataExport.desc")}
        </p>
        <Button onClick={download} disabled={busy}>
          <Download />
          {busy
            ? t("general.dataExport.downloading")
            : t("general.dataExport.button")}
        </Button>
      </div>
    </div>
  );
}

/**
 * Ustaxona ommaviy aloqa raqami — mijoz Mini App'da "Ustaxonam bilan
 * bog'lanish" uchun ko'radi. Bo'sh bo'lsa egasining telefoni ishlatiladi.
 */
function WorkshopContactCard() {
  const { t } = useTranslation(["settings", "common"]);
  const [phone, setPhone] = useState("+998");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.workshopSettings();
        if (!cancelled) setPhone(initialPhoneInput(s.phone));
      } catch {
        /* sukut — forma baribir ko'rinadi */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const s = await api.updateWorkshopSettings({ phone: isBlankPhoneInput(phone) ? null : phone.trim() });
      setPhone(initialPhoneInput(s.phone));
      toast.success(t("general.contact.saved"));
    } catch (e) {
      toast.error((e as { message?: string }).message || t("general.contact.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="settings-card settings-card--contact">
      <CardHeader className="p-4 pb-3">
        <div className="flex items-center gap-2">
          <Phone className="size-5 text-primary" />
          <CardTitle>{t("general.contact.title")}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <p className="text-sm text-muted-foreground mb-3">
          {t("general.contact.desc")}
        </p>
        {loading ? (
          <Skeleton className="h-10 w-full max-w-xs" />
        ) : (
          <div className="flex gap-2 flex-wrap items-center">
            <Input
              type="tel"
              inputMode="tel"
              placeholder={t("general.contact.phonePlaceholder")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="max-w-xs"
            />
            <Button onClick={save} disabled={saving}>
              {t("common:actions.save")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Ustaxona ommaviy profili — manzil, geo-koordinatalar, tavsif, ish vaqti va
 * ijtimoiy tarmoqlar. Kelajakdagi bot ichidagi "yaqin-atrofdagi ustaxona"
 * katalogi uchun zamin. Partial-merge: faqat profil maydonlari yuboriladi,
 * telefon (yuqoridagi karta) tegilmaydi.
 *
 * Filiallari bor ustaxonada bu karta UMUMIY (asosiy) profil — har bir
 * filialning o'z profili Filiallar bo'limidagi formada to'ldiriladi,
 * shu haqda karta ichida eslatma ko'rsatiladi.
 */
function WorkshopProfileCard({ onOpenBranches }: { onOpenBranches: () => void }) {
  const { t } = useTranslation(["settings", "common"]);
  const { branches } = useBranch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [locationUrl, setLocationUrl] = useState("");
  const [description, setDescription] = useState("");
  const [workHours, setWorkHours] = useState("");
  const [instagram, setInstagram] = useState("");
  const [telegram, setTelegram] = useState("");
  const [website, setWebsite] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.workshopSettings();
        if (cancelled) return;
        setAddress(s.address ?? "");
        setLat(s.latitude != null ? String(s.latitude) : "");
        setLng(s.longitude != null ? String(s.longitude) : "");
        setLocationUrl(s.location_url ?? "");
        setDescription(s.description ?? "");
        setWorkHours(s.work_hours ?? "");
        setInstagram(s.instagram ?? "");
        setTelegram(s.telegram ?? "");
        setWebsite(s.website ?? "");
      } catch {
        /* sukut — forma baribir ko'rinadi */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const parseMapsLink = (value: string) => {
    const coordinates = mapCoordinates(value);
    if (coordinates) {
      setLat(String(coordinates[0]));
      setLng(String(coordinates[1]));
      toast.success(t("general.profile.mapsLinkParsed"));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const s = await api.updateWorkshopSettings({
        address: address.trim() || null,
        latitude: lat.trim() === "" ? null : Number(lat),
        longitude: lng.trim() === "" ? null : Number(lng),
        location_url: locationUrl.trim() || null,
        description: description.trim() || null,
        work_hours: workHours.trim() || null,
        instagram: instagram.trim() || null,
        telegram: telegram.trim() || null,
        website: website.trim() || null,
      });
      setLat(s.latitude != null ? String(s.latitude) : "");
      setLng(s.longitude != null ? String(s.longitude) : "");
      setLocationUrl(s.location_url ?? "");
      toast.success(t("general.profile.saved"));
    } catch (e) {
      const er = e as { code?: string; message?: string };
      toast.error(
        er.code === "location_incomplete"
          ? t("general.profile.locationIncomplete")
          : er.message || t("general.profile.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="settings-card settings-card--profile">
      <CardHeader className="p-4 pb-3">
        <div className="flex items-center gap-2">
          <MapPin className="size-5 text-primary" />
          <CardTitle>{t("general.profile.title")}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <p className="text-sm text-muted-foreground mb-3">
          {t("general.profile.desc")}
        </p>
        {branches.length > 0 && (
          <div className="mb-4 rounded-md border bg-muted/50 px-3 py-2.5 text-sm flex items-center justify-between gap-3 flex-wrap">
            <span className="text-muted-foreground">
              {t("general.profile.branchesHint")}
            </span>
            <Button size="sm" variant="outline" onClick={onOpenBranches}>
              {t("general.profile.branchesButton")}
            </Button>
          </div>
        )}
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="wp-address">{t("general.profile.address")}</Label>
              <Input
                id="wp-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t("general.profile.addressPlaceholder")}
              />
            </div>

            <div className="space-y-1.5">
              <Label>{t("general.profile.location")}</Label>
              <Input
                value={locationUrl}
                placeholder={t("general.profile.mapsLinkPlaceholder")}
                onChange={(e) => {
                  const v = e.target.value;
                  setLocationUrl(v);
                  if (v) parseMapsLink(v);
                }}
              />
              <div className="flex gap-2 flex-wrap">
                <Input
                  inputMode="decimal"
                  placeholder={t("general.profile.lat")}
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                  className="max-w-[10rem]"
                />
                <Input
                  inputMode="decimal"
                  placeholder={t("general.profile.lng")}
                  value={lng}
                  onChange={(e) => setLng(e.target.value)}
                  className="max-w-[10rem]"
                />
              </div>
              {locationPreviewUrl(locationUrl, lat, lng) && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(locationPreviewUrl(locationUrl, lat, lng)!, "_blank", "noopener,noreferrer")}
                >
                  <ExternalLink className="mr-1.5 size-4" />
                  {t("general.profile.checkMap")}
                </Button>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="wp-desc">{t("general.profile.description")}</Label>
              <Textarea
                id="wp-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="wp-hours">{t("general.profile.workHours")}</Label>
              <Input
                id="wp-hours"
                value={workHours}
                onChange={(e) => setWorkHours(e.target.value)}
                placeholder={t("general.profile.workHoursPlaceholder")}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="wp-ig">{t("general.profile.instagram")}</Label>
                <Input
                  id="wp-ig"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  placeholder="@..."
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wp-tg">{t("general.profile.telegram")}</Label>
                <Input
                  id="wp-tg"
                  value={telegram}
                  onChange={(e) => setTelegram(e.target.value)}
                  placeholder="@..."
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wp-web">{t("general.profile.website")}</Label>
                <Input
                  id="wp-web"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>

            <Button onClick={save} disabled={saving}>
              {saving ? t("common:actions.saving") : t("common:actions.save")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Anonim mijoz baholari — QR orqali ulangan mijozlardan bot so'ragan yulduz +
 * izoh. Egaga faqat kechikish bilan va identifikatsiyasiz ko'rinadi.
 */
function FeedbacksCard() {
  const { t } = useTranslation("settings");
  const [data, setData] = useState<FeedbackOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.feedbacks();
        if (!cancelled) setData(d);
      } catch {
        /* sukut */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Star className="size-5 text-primary" />
          <CardTitle>{t("feedback.title")}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          {t("feedback.anonNote")}
        </p>
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : !data || data.count === 0 ? (
          <div className="text-sm text-muted-foreground py-4">
            {t("feedback.empty")}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-3xl font-semibold">
                  {data.average != null ? data.average.toFixed(1) : "—"}
                  <span className="text-yellow-500"> ★</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("feedback.average")}
                </div>
              </div>
              <div>
                <div className="text-3xl font-semibold">{data.count}</div>
                <div className="text-xs text-muted-foreground">
                  {t("feedback.countLabel")}
                </div>
              </div>
            </div>

            <div className="space-y-1.5" aria-label={t("feedback.distribution")}>
              {[5, 4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1].map((stars) => {
                const count = data.distribution[stars.toFixed(1)] ?? 0;
                const width = data.count ? (count / data.count) * 100 : 0;
                return (
                  <div key={stars} className="grid grid-cols-[3rem_1fr_2rem] items-center gap-2 text-sm">
                    <span>{stars}★</span>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-yellow-500" style={{ width: `${width}%` }} />
                    </div>
                    <span className="text-right text-muted-foreground">{count}</span>
                  </div>
                );
              })}
            </div>

            <ul className="space-y-2">
              {data.items.map((it) => (
                <li key={it.id} className="rounded-md border p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-yellow-600">{it.stars.toFixed(1)} ★</span>
                    <span className="text-xs text-muted-foreground">
                      {it.date}
                    </span>
                  </div>
                  {it.comment && (
                    <p className="text-sm mt-1 whitespace-pre-wrap">
                      {it.comment}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Yangi parol uchun minimal uzunlik — backend bilan bir xil (auth.py
// MIN_PASSWORD_LENGTH). Bu yerda oldindan tekshiramiz, server baribir
// qayta tekshiradi.
const MIN_PASSWORD_LENGTH = 8;

/**
 * Egasi o'z parolini almashtiradi (ixtiyoriy — majburiy almashtirish ekrani
 * ChangePassword.tsx'da). Sessiya davom etadi: backend tokenni bekor qilmaydi.
 */
function ChangePasswordCard() {
  const { t } = useTranslation(["settings", "common"]);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Darhol ko'rinadigan inline xatolar (foydalanuvchi yozayotganda).
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && next !== confirm;
  const filled = !!current && !!next && !!confirm;
  const liveError = tooShort
    ? t("general.password.tooShort", { count: MIN_PASSWORD_LENGTH })
    : mismatch
      ? t("general.password.mismatch")
      : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!filled || liveError) return;
    setBusy(true);
    try {
      await api.changePassword(current, next, confirm);
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success(t("general.password.saved"));
    } catch (e) {
      const er = e as { code?: string; message?: string };
      setErr(
        er.code === "invalid_current_password" || er.code === "invalid_credentials"
          ? t("general.password.wrongCurrent")
          : er.message || t("general.password.failed"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="settings-card settings-card--security">
      <CardHeader className="p-4 pb-3">
        <div className="flex items-center gap-2">
          <KeyRound className="size-5 text-primary" />
          <CardTitle>{t("general.password.title")}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <p className="text-sm text-muted-foreground mb-3">
          {t("general.password.desc")}
        </p>
        <form onSubmit={submit} className="space-y-3 max-w-xs">
          <div className="space-y-1.5">
            <Label htmlFor="pw-current">{t("general.password.current")}</Label>
            <PasswordInput
              id="pw-current"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              disabled={busy}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw-new">{t("general.password.new")}</Label>
            <PasswordInput
              id="pw-new"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              disabled={busy}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw-confirm">{t("general.password.confirm")}</Label>
            <PasswordInput
              id="pw-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              disabled={busy}
              required
            />
          </div>

          {(liveError || err) && (
            <div className="text-sm rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2">
              {liveError || err}
            </div>
          )}

          <Button type="submit" disabled={busy || !filled || !!liveError}>
            {busy ? t("common:actions.saving") : t("common:actions.save")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
